import React from "react";
import Link from "@/components/LocalizedLink";
import { useTranslation } from "react-i18next";
import SeoHead from "@/components/SeoHead";
import PageShell from "@/components/PageShell";
import PageBanner from "@/components/PageBanner";
import { Shield, Mail, MapPin, ArrowRight } from "lucide-react";
import { openCookiePreferences } from "@/components/CookieBanner";

const BANNER = "https://images.unsplash.com/photo-1497935586351-b67a49e012bf?auto=format&fit=crop&w=2000&q=80";

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="font-serif-display font-bold mb-3" style={{ color: "#2a160c", fontSize: "clamp(1.2rem, 2vw, 1.6rem)" }}>
      {title}
    </h2>
    <div className="prose-cc">{children}</div>
  </section>
);

const PrivacyPolicy = () => {
  const { t, i18n } = useTranslation();
  const locale = (i18n.resolvedLanguage || "it").slice(0, 2);
  const localeMap = { it: "it-IT", en: "en-GB", ja: "ja-JP" };
  return (
    <PageShell>
      <SeoHead pageKey="privacy" image={BANNER} noIndex />
      <PageBanner
        eyebrow={t("privacy.banner.eyebrow")}
        title={t("privacy.banner.title")}
        subtitle={t("privacy.banner.subtitle")}
        image={BANNER}
        height="min(48vh, 420px)"
      />

      <section className="section-y" style={{ background: "#fbf5e9" }}>
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="rounded-2xl p-6 sm:p-8 mb-10" style={{ background: "#f5ead6", border: "1px solid rgba(91,54,33,0.15)" }}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: "#c5371f" }}>
                <Shield size={16} color="#fff" />
              </div>
              <div className="text-sm leading-relaxed" style={{ color: "#3b2417" }}>
                <p>
                  <strong>{t("privacy.titolare")}</strong> {t("privacy.titolareName")}<br />
                  <span className="inline-flex items-center gap-1.5"><MapPin size={12} /> Via Marciotti 84/16, S. Giuseppe Vesuviano (NA), Italia</span><br />
                  <span className="inline-flex items-center gap-1.5"><Mail size={12} /> caffecola1960@libero.it</span>
                </p>
                <p className="mt-2 text-xs opacity-80">
                  {t("privacy.updated")} {new Date().toLocaleDateString(localeMap[locale] || "it-IT")}
                </p>
              </div>
            </div>
          </div>

          <Section title={t("privacy.s1.title")}>
            <p>{t("privacy.s1.p1")}</p>
            <p>{t("privacy.s1.p2")}</p>
          </Section>

          <Section title={t("privacy.s2.title")}>
            <p>{t("privacy.s2.lead")}</p>
            <ul>
              <li dangerouslySetInnerHTML={{ __html: t("privacy.s2.li1") }} />
              <li dangerouslySetInnerHTML={{ __html: t("privacy.s2.li2") }} />
              <li>
                <span dangerouslySetInnerHTML={{ __html: t("privacy.s2.li3Prefix") }} />{" "}
                <Link to="/cookie-policy">{t("footer.cookie")}</Link>.
              </li>
            </ul>
          </Section>

          <Section title={t("privacy.s3.title")}>
            <ul>
              <li dangerouslySetInnerHTML={{ __html: t("privacy.s3.li1") }} />
              <li dangerouslySetInnerHTML={{ __html: t("privacy.s3.li2") }} />
              <li dangerouslySetInnerHTML={{ __html: t("privacy.s3.li3") }} />
            </ul>
          </Section>

          <Section title={t("privacy.s4.title")}>
            <p>{t("privacy.s4.p1")}</p>
          </Section>

          <Section title={t("privacy.s5.title")}>
            <p>{t("privacy.s5.p1")}</p>
          </Section>

          <Section title={t("privacy.s6.title")}>
            <p>{t("privacy.s6.p1")}</p>
          </Section>

          <Section title={t("privacy.s7.title")}>
            <p>{t("privacy.s7.lead")}</p>
            <ul>
              <li>{t("privacy.s7.li1")}</li>
              <li>{t("privacy.s7.li2")}</li>
              <li>{t("privacy.s7.li3")}</li>
              <li>{t("privacy.s7.li4")}</li>
              <li>{t("privacy.s7.li5")}</li>
            </ul>
            <p>
              {t("privacy.s7.p1Prefix")} <a href="mailto:caffecola1960@libero.it">caffecola1960@libero.it</a>.
            </p>
          </Section>

          <Section title={t("privacy.s8.title")}>
            <p>{t("privacy.s8.p1")}</p>
          </Section>

          <Section title={t("privacy.s9.title")}>
            <p>{t("privacy.s9.p1")}</p>
          </Section>

          <div className="rounded-2xl p-6 sm:p-8 mt-8 text-center" style={{ background: "#2a160c", color: "#f5ead6" }}>
            <p className="font-serif-display text-lg">{t("privacy.managePrefs")}</p>
            <button
              onClick={openCookiePreferences}
              className="btn-red mt-4 inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-semibold uppercase tracking-wider text-xs"
            >
              {t("privacy.manageBtn")} <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export default PrivacyPolicy;
